import React from 'react';
import {
  Box,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';


const SalonLayoutOptionGroup = ({
  legend,
  name,
  value,
  options,
  onChange,
  disabled = false,
  error = '',
  focusRef,
  selectedLabel = 'Selected',
  renderThumbnail,
}) => {
  const errorId = `${name}-error`;

  return (
    <Box
      component="fieldset"
      aria-describedby={error ? errorId : undefined}
      aria-invalid={error ? 'true' : undefined}
      sx={{ m: 0, p: 0, minWidth: 0, border: 0 }}
    >
      <Typography
        component="legend"
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: 800 }}
      >
        {legend}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: `repeat(${Math.min(options.length, 3)}, minmax(0, 1fr))`,
          },
          gap: 1,
        }}
      >
        {options.map((option, index) => {
          const selected = option.value === value;
          const inputId = `${name}-${option.value}`;

          return (
            <Box
              key={option.value}
              component="label"
              htmlFor={inputId}
              data-testid={`salon-layout-option-${option.value}`}
              style={{ minHeight: '44px' }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'stretch',
                p: 1.25,
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 2,
                backgroundColor: selected ? 'primary.50' : 'background.paper',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.56 : 1,
                '&:focus-within': {
                  outline: '3px solid',
                  outlineColor: 'primary.light',
                  outlineOffset: '2px',
                },
              }}
            >
              <input
                ref={index === 0 ? focusRef : undefined}
                id={inputId}
                type="radio"
                name={name}
                value={option.value}
                checked={selected}
                disabled={disabled}
                onChange={() => {
                  if (!disabled) onChange(option.value);
                }}
                onKeyDown={(event) => {
                  if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onChange(option.value);
                  }
                }}
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
              />
              <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
                {renderThumbnail && (
                  <Box aria-hidden="true">{renderThumbnail(option, selected)}</Box>
                )}
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
                {selected && (
                  <Typography
                    aria-hidden="true"
                    variant="caption"
                    sx={{ color: 'primary.main', fontWeight: 800 }}
                  >
                    {selectedLabel}
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
      {error && (
        <FormHelperText id={errorId} error role="alert" sx={{ mx: 0, mt: 0.75 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default SalonLayoutOptionGroup;
